import {
    ConnectionPositionPair,
    Overlay,
    OverlayConfig,
} from "@angular/cdk/overlay";
import { ComponentPortal, PortalInjector } from "@angular/cdk/portal";
import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    InjectionToken,
    Injector,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
} from "@angular/core";
import {
    BehaviorSubject,
    map,
    Subject,
    take,
    takeUntil,
    tap,
    withLatestFrom,
} from "rxjs";
import { UsersDropdownComponent } from "../users-dropdown/users-dropdown.component";
export const USER_SELECT_PARAMS = new InjectionToken<any>("USER_SELECT_PARAMS");

@Component({
    selector: "app-user-select",
    templateUrl: "./user-select.component.html",
    styleUrls: ["./user-select.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserSelectComponent implements OnInit, OnDestroy {
    closeModal$ = new Subject();
    @Input("users")
    set userList(value: any[]) {
        if (Array.isArray(value)) {
            if (value.length > 6) {
                this.usersForModal = value.slice(6);
            }
            this.users = value.slice(0, 6);
        }
    }

    defaultColour = 'rgb(46, 121, 126)';

    users: any[] | undefined = [];
    usersForModal: any[] | undefined = [];

    @Output() change = new EventEmitter<string[]>();
    @ViewChild("openModal") openModalRef: ElementRef;

    destroy$ = new Subject<void>();
    selected$ = new BehaviorSubject([]);
    isRestSelected$ = this.selected$.pipe(
        map(
            (selected) =>
                !!selected.filter((item) =>
                    this.usersForModal.some((users) => users.id === item)
                )?.length
        )
    );

    constructor(private overlay: Overlay, private injector: Injector) {}

    ngOnInit(): void {
        this.selected$

            .pipe(takeUntil(this.destroy$))
            .subscribe((values) => this.change.emit(values));
    }
    getInitials(name: string) {
        const parsedName = name.split(/\s+/);
        if (!parsedName.length) {
            return "";
        }
        if (parsedName.length > 1) {
            return `${parsedName[0].charAt(0).toUpperCase()}${parsedName[1]
                .charAt(0)
                .toUpperCase()}`;
        }
        return `${parsedName[0].charAt(0).toUpperCase()}`;
    }

    select(id: string) {
        this.selected$.pipe(take(1)).subscribe((current) => {
            const isSelected = this.isSelected(id);
            if (isSelected) {
                const idx = current.indexOf(id);
                if (idx !== -1) {
                    current.splice(idx, 1);
                    this.selected$.next(current);
                }
                return;
            }
            current.push(id);
            this.selected$.next(current);
        });
    }
    isSelected(id: string) {
        return this.selected$.value.includes(id);
    }

    showModal() {
        const modalData = {
            users: this.usersForModal,
            select: this.select.bind(this),
            isSelected: this.isSelected.bind(this),
            close$: this.closeModal$,
        };

        const positionStrategy = this.overlay
            .position()
            .flexibleConnectedTo(this.openModalRef.nativeElement)
            .withPositions([
                new ConnectionPositionPair(
                    { originX: "center", originY: "center" },
                    {
                        overlayX: "start",
                        overlayY: "top",
                    }
                ),
            ]);
        const scrollStrategy = this.overlay.scrollStrategies.reposition();

        const dialogRef = this.overlay.create(
            new OverlayConfig({
                disposeOnNavigation: true,
                scrollStrategy,
                positionStrategy,
                hasBackdrop: false,
                panelClass: "select-users-dialog",
                height: 342,
            })
        );
        this.destroy$.subscribe(() => dialogRef.dispose());
        const portal = new ComponentPortal(
            UsersDropdownComponent,
            null,
            this.createInjector(modalData)
        );
        const componentRef = dialogRef.attach(portal);
        setTimeout(() => {
            this.closeModal$.pipe(takeUntil(this.destroy$)).subscribe(() => {
                dialogRef.dispose();
            });
        }, 0);
    }
    private createInjector(params: any): PortalInjector {
        const weakMap = new WeakMap<any, any>([[USER_SELECT_PARAMS, params]]);
        return new PortalInjector(this.injector, weakMap);
    }

    getUserColour(user) {
        return user.avatarColour?.length ? user.avatarColour : this.defaultColour;
    }

    ngOnDestroy(): void {
        this.destroy$.next();
    }
}
